import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Chip, IconButton, InputAdornment, LinearProgress, TextField, Tooltip, Typography,
} from '@mui/material';
import { Refresh, Search } from '@mui/icons-material';
import axios from '../utils/axios';
import CandleChartModal from './CandleChartModal';
import TickerLink from './TickerLink';
import DataTable, { DataTableColumn } from '../design/DataTable';
import PnlText from '../design/PnlText';
import { formatCurrency, formatDateTime } from '../design/format';

interface Transaction {
  Id: number;
  Time: string;
  Ticker: string;
  Action: string;
  Price: number;
  Investment: number;
  ProfitAndLossPercent: number;
  ProfitAndLoss: number;
  MarketCap: number;
  Volume: number;
  ChangePercent: number;
  PE: number;
  SellReason: string | null;
}

interface TransactionHistoryProps {
  traderId?: number;
}

const ITEMS_PER_PAGE = 10;

const SELL_REASON_LABELS: Record<string, string> = {
  max_loss: 'Max loss',
  max_profit: 'Max profit',
  score_flip: 'Score flip',
  max_days_held: 'Max days held',
  pending_order: 'Pending order',
};

const COLUMNS: DataTableColumn<Transaction>[] = [
  { key: 'Time', label: 'Time', sortable: true, minWidth: 150, render: (t) => formatDateTime(t.Time) },
  {
    key: 'Ticker', label: 'Ticker', sortable: true, minWidth: 80,
    render: (t) => <TickerLink ticker={t.Ticker} />,
  },
  {
    key: 'Action', label: 'Action', sortable: true, minWidth: 80,
    render: (t) => (
      <Chip label={t.Action} color={t.Action === 'BUY' ? 'success' : 'error'} size="small" />
    ),
  },
  { key: 'Price', label: 'Price', sortable: true, minWidth: 100, render: (t) => formatCurrency(t.Price) },
  { key: 'Investment', label: 'Investment', sortable: true, minWidth: 110, render: (t) => formatCurrency(t.Investment) },
  {
    key: 'ProfitAndLoss', label: 'P&L', sortable: true, minWidth: 100,
    render: (t) => <PnlText value={t.ProfitAndLoss} format="currency" variant="body2" />,
  },
  {
    key: 'ProfitAndLossPercent', label: 'P&L %', sortable: true, minWidth: 80,
    // API value is a fraction (0.0123 = 1.23%); scale to percent units for PnlText.
    render: (t) => <PnlText value={t.ProfitAndLossPercent * 100} format="percent" variant="body2" />,
  },
  {
    // Not in the server's sort whitelist, so deliberately not sortable.
    key: 'SellReason', label: 'Reason', sortable: false, minWidth: 110,
    render: (t) => (
      <Typography variant="body2" color="text.secondary">
        {(t.SellReason && SELL_REASON_LABELS[t.SellReason]) || '—'}
      </Typography>
    ),
  },
];

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ traderId = 1 }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [orderBy, setOrderBy] = useState<keyof Transaction>('Time');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [error, setError] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const fetchSeq = useRef(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTransactions = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: String((page - 1) * ITEMS_PER_PAGE),
        limit: String(ITEMS_PER_PAGE),
        trader_id: String(traderId),
        sort_by: orderBy,
        sort_order: order,
      });
      if (debouncedSearch) params.set('ticker', debouncedSearch);
      const response = await axios.get(`/transactions?${params.toString()}`);
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      setTransactions(response.data.transactions || response.data);
      setTotalCount(response.data.total ?? response.data.length ?? 0);
      setError(null);
    } catch (err) {
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      console.error('Error fetching transactions:', err);
      setError('Failed to fetch transactions');
    } finally {
      if (seq === fetchSeq.current) {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [page, traderId, orderBy, order, debouncedSearch]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Clamp the page when totalCount shrinks under the user (e.g. after a refresh
  // returns fewer rows); the fetch effect then refetches the clamped page.
  useEffect(() => {
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
    if (!loading && totalPages >= 1 && page > totalPages) setPage(totalPages);
  }, [loading, totalCount, page]);

  const handleSort = (key: string) => {
    const property = key as keyof Transaction;
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(1);
  };

  const pageCount = Math.ceil(totalCount / ITEMS_PER_PAGE);

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark', mb: 3 }}>
          Transaction History
        </Typography>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error" variant="h6" mb={2}>
            {error}
          </Typography>
          <Tooltip title="Refresh transactions">
            <IconButton onClick={() => fetchTransactions()} color="primary" aria-label="Retry">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
          Transaction History
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip label={`${totalCount} transactions`} color="primary" variant="outlined" size="small" />
          <Tooltip title="Refresh transactions">
            <IconButton onClick={() => fetchTransactions()} size="small" color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TextField
        size="small"
        placeholder="Search ticker…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, width: 220 }}
      />

      {loading && hasLoadedOnce && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      <DataTable
        columns={COLUMNS}
        rows={transactions}
        rowKey={(t) => t.Id}
        loading={loading && !hasLoadedOnce}
        skeletonRows={10}
        emptyMessage={
          debouncedSearch ? `No transactions match "${debouncedSearch}"` : "This trader hasn't made any transactions yet."
        }
        sortBy={orderBy}
        sortDir={order}
        onSortChange={handleSort}
        onRowClick={(t) => setSelectedTransaction(t)}
        rowAriaLabel={(t) => `View chart for ${t.Ticker}`}
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
      />

      <CandleChartModal
        open={selectedTransaction !== null}
        onClose={() => setSelectedTransaction(null)}
        ticker={selectedTransaction?.Ticker ?? ''}
        tradeTime={selectedTransaction?.Time ?? null}
        action={selectedTransaction?.Action === 'SELL' ? 'SELL' : 'BUY'}
        tradePrice={selectedTransaction?.Price ?? null}
        traderId={traderId}
        profitAndLossPercent={
          selectedTransaction != null ? selectedTransaction.ProfitAndLossPercent * 100 : null
        }
        profitAndLoss={selectedTransaction?.ProfitAndLoss ?? null}
        volume={selectedTransaction?.Volume ?? null}
        marketCap={selectedTransaction?.MarketCap ?? null}
      />
    </Box>
  );
};

export default TransactionHistory;
